import {
  Circle, HelpCircle, FileCode, AlertTriangle, Layers,
  Workflow, Split, GitBranch, GitMerge, GitFork, Merge, RotateCw,
  Database, Server, HardDrive, Archive, Boxes, Cloud,
  Code2, Terminal, Bug, Settings, TestTube, Rocket, Package, Puzzle,
  Network, Cpu, Monitor, Shield, Waypoints,
  MessageSquare, Bell, Zap, Webhook, Globe,
  LayoutDashboard, Component, FileText, FormInput, Tablet,
  User, Users, UserCheck, Building2, ShieldCheck,
  StickyNote, Info, Link, BookOpen, Target, Crosshair,
  CheckCircle, XCircle, Clock, Timer, Play, Pause,
  ServerCrash, Radio, FolderOpen, Box, Type, Square,
  Brain, Computer, Disc3, Router, BrickWall, EthernetPort,
} from "lucide-react";
import { Icon as IconifyIcon } from "@iconify/react";
import { getBrandColor } from "@/config/iconCatalog";

export interface NodeItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  category: string;
  iconOverride?: string;  // Iconify ID pre-set on the dropped node
  keywords?: string[];    // Synonyms matched by search (e.g. "pc" → Computer)
}

export interface ShapeItem {
  key: string;
  label: string;
  preview: React.ReactNode;
  keywords?: string[];    // Synonyms matched by search
}

// ── Node items ───────────────────────────────────────────────────────────────

export const NODE_ITEMS: NodeItem[] = [
  // Core (defaults shown first)
  { key: "concept",      label: "Concept",      icon: <Box size={14} />,            category: "Core" },
  { key: "question",     label: "Question",     icon: <HelpCircle size={14} />,     category: "Core" },
  { key: "example",      label: "Example",      icon: <FileCode size={14} />,       category: "Core" },
  { key: "warning",      label: "Warning",      icon: <AlertTriangle size={14} />,  category: "Core" },
  { key: "deep-dive",    label: "Deep Dive",    icon: <Layers size={14} />,         category: "Core" },
  { key: "note",         label: "Note",         icon: <StickyNote size={14} />,     category: "Core" },
  { key: "info",         label: "Info",         icon: <Info size={14} />,           category: "Core" },
  { key: "brain",        label: "Brain",        icon: <Brain size={14} />,          category: "Core" },
  // Process / Flow
  { key: "process",      label: "Process",      icon: <Workflow size={14} />,       category: "Flow" },
  { key: "decision",     label: "Decision",     icon: <Split size={14} />,          category: "Flow" },
  { key: "fork",         label: "Fork",         icon: <GitFork size={14} />,        category: "Flow" },
  { key: "merge",        label: "Merge",        icon: <Merge size={14} />,          category: "Flow" },
  { key: "branch",       label: "Branch",       icon: <GitBranch size={14} />,      category: "Flow" },
  { key: "gateway",      label: "Gateway",      icon: <GitMerge size={14} />,       category: "Flow" },
  { key: "loop",         label: "Loop",         icon: <RotateCw size={14} />,       category: "Flow" },
  { key: "trigger",      label: "Trigger",      icon: <Play size={14} />,           category: "Flow" },
  { key: "pause",        label: "Pause",        icon: <Pause size={14} />,          category: "Flow" },
  // Data / Storage
  { key: "data",         label: "Data",         icon: <Database size={14} />,       category: "Data" },
  { key: "database",     label: "Database",     icon: <Database size={14} />,       category: "Data", keywords: ["db", "sql", "datastore"] },
  { key: "server",       label: "Server",       icon: <Server size={14} />,         category: "Data", keywords: ["host", "machine", "backend"] },
  { key: "storage",      label: "Storage",      icon: <HardDrive size={14} />,      category: "Data", keywords: ["disk", "hdd", "ssd", "drive"] },
  { key: "disk",         label: "Disk",         icon: <Disc3 size={14} />,          category: "Data", keywords: ["hdd", "ssd", "drive", "hard drive", "storage"] },
  { key: "volume",       label: "Volume",       icon: <HardDrive size={14} />,      category: "Data", keywords: ["mount", "partition", "storage", "disk"] },
  { key: "cache",        label: "Cache",        icon: <Archive size={14} />,        category: "Data", keywords: ["redis", "memcached", "buffer"] },
  { key: "queue",        label: "Queue",        icon: <Boxes size={14} />,          category: "Data", keywords: ["mq", "buffer", "sqs", "message queue"] },
  { key: "cloud",        label: "Cloud",        icon: <Cloud size={14} />,          category: "Data" },
  { key: "folder",       label: "Folder",       icon: <FolderOpen size={14} />,     category: "Data", keywords: ["directory", "dir"] },
  // Code / Dev
  { key: "code",         label: "Code",         icon: <Code2 size={14} />,          category: "Dev" },
  { key: "terminal",     label: "Terminal",     icon: <Terminal size={14} />,       category: "Dev" },
  { key: "bug",          label: "Bug",          icon: <Bug size={14} />,            category: "Dev" },
  { key: "config",       label: "Config",       icon: <Settings size={14} />,       category: "Dev" },
  { key: "test",         label: "Test",         icon: <TestTube size={14} />,       category: "Dev" },
  { key: "deploy",       label: "Deploy",       icon: <Rocket size={14} />,         category: "Dev" },
  { key: "package",      label: "Package",      icon: <Package size={14} />,        category: "Dev" },
  { key: "module",       label: "Module",       icon: <Puzzle size={14} />,         category: "Dev" },
  // Infrastructure
  { key: "network",      label: "Network",      icon: <Network size={14} />,        category: "Infra", keywords: ["lan", "wan", "ethernet", "vpc", "subnet"] },
  { key: "router",       label: "Router",       icon: <Router size={14} />,         category: "Infra", keywords: ["gateway", "network", "modem"] },
  { key: "switch",       label: "Switch",       icon: <EthernetPort size={14} />,   category: "Infra", keywords: ["network", "lan", "hub", "port"] },
  { key: "firewall",     label: "Firewall",     icon: <BrickWall size={14} />,      category: "Infra", keywords: ["security", "waf", "shield", "network"] },
  { key: "cpu",          label: "CPU",          icon: <Cpu size={14} />,            category: "Infra", keywords: ["processor", "compute", "core"] },
  { key: "monitor",      label: "Monitor",      icon: <Monitor size={14} />,        category: "Infra", keywords: ["screen", "display"] },
  { key: "computer",     label: "Computer",     icon: <Computer size={14} />,       category: "Infra", keywords: ["pc", "desktop", "workstation", "machine"] },
  { key: "shield",       label: "Shield",       icon: <Shield size={14} />,         category: "Infra", keywords: ["security", "protection"] },
  { key: "load-balancer", label: "Load Balancer", icon: <Waypoints size={14} />,    category: "Infra", keywords: ["lb", "nginx", "haproxy", "proxy"] },
  { key: "microservice", label: "Microservice", icon: <ServerCrash size={14} />,    category: "Infra", keywords: ["service", "svc", "micro"] },
  { key: "pubsub",       label: "Pub/Sub",      icon: <Radio size={14} />,          category: "Infra", keywords: ["pub sub", "topic", "broker", "kafka"] },
  // Communication / Events
  { key: "message",      label: "Message",      icon: <MessageSquare size={14} />,  category: "Events" },
  { key: "event",        label: "Event",        icon: <Zap size={14} />,            category: "Events" },
  { key: "webhook",      label: "Webhook",      icon: <Webhook size={14} />,        category: "Events" },
  { key: "notification", label: "Notify",       icon: <Bell size={14} />,           category: "Events" },
  { key: "api",          label: "API",          icon: <Globe size={14} />,          category: "Events" },
  // UI / Interface
  { key: "screen",       label: "Screen",       icon: <Tablet size={14} />,         category: "UI" },
  { key: "component",    label: "Component",    icon: <Component size={14} />,      category: "UI" },
  { key: "page",         label: "Page",         icon: <LayoutDashboard size={14} />, category: "UI" },
  { key: "form",         label: "Form",         icon: <FormInput size={14} />,      category: "UI" },
  // People / Org
  { key: "actor",        label: "Actor",        icon: <User size={14} />,           category: "People" },
  { key: "user",         label: "User",         icon: <User size={14} />,           category: "People" },
  { key: "team",         label: "Team",         icon: <Users size={14} />,          category: "People" },
  { key: "org",          label: "Org",          icon: <Building2 size={14} />,      category: "People" },
  { key: "role",         label: "Role",         icon: <UserCheck size={14} />,      category: "People" },
  { key: "auth",         label: "Auth",         icon: <ShieldCheck size={14} />,    category: "People" },
  // Reference
  { key: "reference",    label: "Reference",    icon: <BookOpen size={14} />,       category: "Ref" },
  { key: "link",         label: "Link",         icon: <Link size={14} />,           category: "Ref" },
  { key: "source",       label: "Source",       icon: <FileText size={14} />,       category: "Ref" },
  { key: "target",       label: "Target",       icon: <Target size={14} />,         category: "Ref" },
  { key: "goal",         label: "Goal",         icon: <Crosshair size={14} />,      category: "Ref" },
  // Status
  { key: "success",      label: "Success",      icon: <CheckCircle size={14} />,    category: "Status" },
  { key: "error",        label: "Error",        icon: <XCircle size={14} />,        category: "Status" },
  { key: "timer",        label: "Timer",        icon: <Timer size={14} />,          category: "Status" },
  { key: "cron",         label: "Cron",         icon: <Clock size={14} />,          category: "Status" },
  // Cloud providers
  { key: "aws",          label: "AWS",          icon: <IconifyIcon icon="logos:aws" width={14} height={14} />,                     category: "Cloud", iconOverride: "logos:aws" },
  { key: "aws-s3",       label: "S3",           icon: <IconifyIcon icon="logos:aws-s3" width={14} height={14} />,                  category: "Cloud", iconOverride: "logos:aws-s3" },
  { key: "aws-lambda",   label: "Lambda",       icon: <IconifyIcon icon="logos:aws-lambda" width={14} height={14} />,              category: "Cloud", iconOverride: "logos:aws-lambda" },
  { key: "aws-ec2",      label: "EC2",          icon: <IconifyIcon icon="logos:aws-ec2" width={14} height={14} />,                 category: "Cloud", iconOverride: "logos:aws-ec2" },
  { key: "aws-rds",      label: "RDS",          icon: <IconifyIcon icon="logos:aws-rds" width={14} height={14} />,                 category: "Cloud", iconOverride: "logos:aws-rds" },
  { key: "aws-dynamodb", label: "DynamoDB",     icon: <IconifyIcon icon="logos:aws-dynamodb" width={14} height={14} />,            category: "Cloud", iconOverride: "logos:aws-dynamodb" },
  { key: "aws-sqs",      label: "SQS",          icon: <IconifyIcon icon="logos:aws-sqs" width={14} height={14} />,                 category: "Cloud", iconOverride: "logos:aws-sqs" },
  { key: "gcp",          label: "Google Cloud", icon: <IconifyIcon icon="logos:google-cloud" width={14} height={14} />,            category: "Cloud", iconOverride: "logos:google-cloud" },
  { key: "azure",        label: "Azure",        icon: <IconifyIcon icon="logos:microsoft-azure" width={14} height={14} />,         category: "Cloud", iconOverride: "logos:microsoft-azure" },
  // Databases
  { key: "clickhouse",   label: "ClickHouse",   icon: <IconifyIcon icon="simple-icons:clickhouse" width={14} height={14} color={getBrandColor("simple-icons:clickhouse")} />,  category: "Tech", iconOverride: "simple-icons:clickhouse" },
  { key: "postgresql",   label: "PostgreSQL",   icon: <IconifyIcon icon="logos:postgresql" width={14} height={14} />,              category: "Tech", iconOverride: "logos:postgresql" },
  { key: "mysql",        label: "MySQL",        icon: <IconifyIcon icon="logos:mysql" width={14} height={14} />,                   category: "Tech", iconOverride: "logos:mysql" },
  { key: "mongodb",      label: "MongoDB",      icon: <IconifyIcon icon="logos:mongodb" width={14} height={14} />,                 category: "Tech", iconOverride: "logos:mongodb" },
  { key: "redis",        label: "Redis",        icon: <IconifyIcon icon="logos:redis" width={14} height={14} />,                   category: "Tech", iconOverride: "logos:redis" },
  { key: "elasticsearch",label: "Elasticsearch",icon: <IconifyIcon icon="logos:elasticsearch" width={14} height={14} />,           category: "Tech", iconOverride: "logos:elasticsearch" },
  { key: "kafka",        label: "Kafka",        icon: <IconifyIcon icon="logos:apache-kafka" width={14} height={14} />,            category: "Tech", iconOverride: "logos:apache-kafka" },
  { key: "rabbitmq",     label: "RabbitMQ",     icon: <IconifyIcon icon="logos:rabbitmq" width={14} height={14} />,                category: "Tech", iconOverride: "logos:rabbitmq" },
  // Infra
  { key: "nginx",        label: "Nginx",        icon: <IconifyIcon icon="logos:nginx" width={14} height={14} />,                   category: "Tech", iconOverride: "logos:nginx" },
  { key: "docker",       label: "Docker",       icon: <IconifyIcon icon="logos:docker-icon" width={14} height={14} />,             category: "Tech", iconOverride: "logos:docker-icon" },
  { key: "kubernetes",   label: "Kubernetes",   icon: <IconifyIcon icon="logos:kubernetes" width={14} height={14} />,              category: "Tech", iconOverride: "logos:kubernetes" },
  { key: "prometheus",   label: "Prometheus",   icon: <IconifyIcon icon="logos:prometheus" width={14} height={14} />,              category: "Tech", iconOverride: "logos:prometheus" },
  { key: "grafana",      label: "Grafana",      icon: <IconifyIcon icon="logos:grafana" width={14} height={14} />,                 category: "Tech", iconOverride: "logos:grafana" },
  { key: "terraform",    label: "Terraform",    icon: <IconifyIcon icon="logos:terraform-icon" width={14} height={14} />,          category: "Tech", iconOverride: "logos:terraform-icon" },
  // Languages
  { key: "go-lang",      label: "Go",           icon: <IconifyIcon icon="logos:go" width={14} height={14} />,                      category: "Tech", iconOverride: "logos:go" },
  { key: "python-lang",  label: "Python",       icon: <IconifyIcon icon="logos:python" width={14} height={14} />,                  category: "Tech", iconOverride: "logos:python" },
  { key: "nodejs-lang",  label: "Node.js",      icon: <IconifyIcon icon="logos:nodejs" width={14} height={14} />,                  category: "Tech", iconOverride: "logos:nodejs" },
  { key: "typescript-lang", label: "TypeScript",icon: <IconifyIcon icon="logos:typescript-icon" width={14} height={14} />,         category: "Tech", iconOverride: "logos:typescript-icon" },
  { key: "rust-lang",    label: "Rust",         icon: <IconifyIcon icon="logos:rust" width={14} height={14} />,                    category: "Tech", iconOverride: "logos:rust" },
  { key: "graphql-api",  label: "GraphQL",      icon: <IconifyIcon icon="logos:graphql" width={14} height={14} />,                 category: "Tech", iconOverride: "logos:graphql" },
];

// Keys shown when no search query (first N defaults)
export const DEFAULT_NODE_KEYS = NODE_ITEMS.slice(0, 8).map((n) => n.key);

// ── Shape items ───────────────────────────────────────────────────────────────

const SZ = 36;

// Per-shape accent colors for the dark toolbar popup context
const C = {
  slate:   { fill: "rgba(148,163,184,0.15)", stroke: "#94a3b8" },
  indigo:  { fill: "rgba(99,102,241,0.2)",   stroke: "#818cf8" },
  amber:   { fill: "rgba(251,191,36,0.2)",   stroke: "#fbbf24" },
  emerald: { fill: "rgba(16,185,129,0.2)",   stroke: "#34d399" },
  rose:    { fill: "rgba(244,63,94,0.2)",    stroke: "#fb7185" },
  sky:     { fill: "rgba(14,165,233,0.2)",   stroke: "#38bdf8" },
  teal:    { fill: "rgba(20,184,166,0.2)",   stroke: "#2dd4bf" },
  orange:  { fill: "rgba(249,115,22,0.2)",   stroke: "#fb923c" },
  fuchsia: { fill: "rgba(217,70,239,0.2)",   stroke: "#e879f9" },
};

export const SHAPE_ITEMS: ShapeItem[] = [
  {
    key: "textbox",
    label: "Text",
    preview: (
      <svg width={SZ} height={SZ} viewBox="0 0 40 40" aria-hidden>
        <rect x={2} y={8} width={36} height={24} rx={3} fill={C.slate.fill} stroke={C.slate.stroke} strokeWidth={1.5} />
        <line x1={8} y1={16} x2={32} y2={16} stroke={C.slate.stroke} strokeWidth={1.2} />
        <line x1={8} y1={22} x2={26} y2={22} stroke={C.slate.stroke} strokeWidth={1.2} />
      </svg>
    ),
  },
  {
    key: "container_box",
    label: "Container",
    preview: (
      <svg width={SZ} height={SZ} viewBox="0 0 40 40" aria-hidden>
        <rect x={2} y={2} width={36} height={36} rx={4} fill={C.slate.fill} stroke={C.slate.stroke} strokeWidth={1.5} strokeDasharray="4 3" />
      </svg>
    ),
  },
  {
    key: "circle",
    label: "Circle",
    preview: (
      <svg width={SZ} height={SZ} viewBox="0 0 40 40" aria-hidden>
        <circle cx={20} cy={20} r={17} fill={C.indigo.fill} stroke={C.indigo.stroke} strokeWidth={1.5} />
      </svg>
    ),
  },
  {
    key: "diamond",
    label: "Diamond",
    preview: (
      <svg width={SZ} height={SZ} viewBox="0 0 40 40" aria-hidden>
        <polygon points="20,2 38,20 20,38 2,20" fill={C.amber.fill} stroke={C.amber.stroke} strokeWidth={1.5} />
      </svg>
    ),
  },
  {
    key: "hexagon",
    label: "Hexagon",
    preview: (
      <svg width={SZ} height={SZ} viewBox="0 0 40 40" aria-hidden>
        <polygon points="20,2 36,11 36,29 20,38 4,29 4,11" fill={C.emerald.fill} stroke={C.emerald.stroke} strokeWidth={1.5} />
      </svg>
    ),
  },
  {
    key: "triangle",
    label: "Triangle",
    preview: (
      <svg width={SZ} height={SZ} viewBox="0 0 40 40" aria-hidden>
        <polygon points="20,3 38,37 2,37" fill={C.rose.fill} stroke={C.rose.stroke} strokeWidth={1.5} />
      </svg>
    ),
  },
  {
    key: "parallelogram",
    label: "Parallel",
    preview: (
      <svg width={SZ} height={SZ} viewBox="0 0 40 40" aria-hidden>
        <polygon points="10,6 38,6 30,34 2,34" fill={C.sky.fill} stroke={C.sky.stroke} strokeWidth={1.5} />
      </svg>
    ),
  },
  {
    key: "cylinder",
    label: "Cylinder",
    preview: (
      <svg width={SZ} height={SZ} viewBox="0 0 40 40" aria-hidden>
        <rect x={4} y={10} width={32} height={22} fill={C.teal.fill} stroke="none" />
        <ellipse cx={20} cy={32} rx={16} ry={5} fill={C.teal.fill} stroke={C.teal.stroke} strokeWidth={1.5} />
        <ellipse cx={20} cy={10} rx={16} ry={5} fill={C.teal.fill} stroke={C.teal.stroke} strokeWidth={1.5} />
        <line x1={4} y1={10} x2={4} y2={32} stroke={C.teal.stroke} strokeWidth={1.5} />
        <line x1={36} y1={10} x2={36} y2={32} stroke={C.teal.stroke} strokeWidth={1.5} />
      </svg>
    ),
  },
  {
    key: "arrow_right",
    label: "Arrow",
    preview: (
      <svg width={SZ} height={SZ} viewBox="0 0 40 40" aria-hidden>
        <polygon points="2,13 28,13 28,5 38,20 28,35 28,27 2,27" fill={C.orange.fill} stroke={C.orange.stroke} strokeWidth={1.5} />
      </svg>
    ),
  },
  {
    key: "cross",
    label: "Cross",
    preview: (
      <svg width={SZ} height={SZ} viewBox="0 0 40 40" aria-hidden>
        <polygon points="14,2 26,2 26,14 38,14 38,26 26,26 26,38 14,38 14,26 2,26 2,14 14,14" fill={C.fuchsia.fill} stroke={C.fuchsia.stroke} strokeWidth={1.5} />
      </svg>
    ),
  },

  // ── Flowchart primitives ────────────────────────────────────────────────────
  {
    key: "rounded_rect",
    label: "Rounded Rect",
    preview: (
      <svg width={SZ} height={SZ} viewBox="0 0 40 40" aria-hidden>
        <rect x={3} y={8} width={34} height={24} rx={6} fill={C.slate.fill} stroke={C.slate.stroke} strokeWidth={1.5} />
      </svg>
    ),
  },
  {
    key: "rectangle",
    label: "Rectangle",
    preview: (
      <svg width={SZ} height={SZ} viewBox="0 0 40 40" aria-hidden>
        <rect x={3} y={8} width={34} height={24} rx={1} fill={C.slate.fill} stroke={C.slate.stroke} strokeWidth={1.5} />
      </svg>
    ),
  },
  {
    key: "pill",
    label: "Terminator",
    preview: (
      <svg width={SZ} height={SZ} viewBox="0 0 40 40" aria-hidden>
        <rect x={2} y={12} width={36} height={16} rx={8} fill={C.emerald.fill} stroke={C.emerald.stroke} strokeWidth={1.5} />
      </svg>
    ),
  },
  {
    key: "ellipse",
    label: "Ellipse",
    preview: (
      <svg width={SZ} height={SZ} viewBox="0 0 40 40" aria-hidden>
        <ellipse cx={20} cy={20} rx={18} ry={12} fill={C.indigo.fill} stroke={C.indigo.stroke} strokeWidth={1.5} />
      </svg>
    ),
  },
  {
    key: "document",
    label: "Document",
    preview: (
      <svg width={SZ} height={SZ} viewBox="0 0 40 40" aria-hidden>
        <path d="M4,6 H36 V30 Q30,36 24,30 Q18,24 12,30 Q7,35 4,30 Z" fill={C.sky.fill} stroke={C.sky.stroke} strokeWidth={1.5} />
      </svg>
    ),
  },
  {
    key: "predefined_process",
    label: "Subroutine",
    preview: (
      <svg width={SZ} height={SZ} viewBox="0 0 40 40" aria-hidden>
        <rect x={3} y={9} width={34} height={22} fill={C.slate.fill} stroke={C.slate.stroke} strokeWidth={1.5} />
        <line x1={9} y1={9} x2={9} y2={31} stroke={C.slate.stroke} strokeWidth={1.5} />
        <line x1={31} y1={9} x2={31} y2={31} stroke={C.slate.stroke} strokeWidth={1.5} />
      </svg>
    ),
  },
  {
    key: "manual_input",
    label: "Manual Input",
    preview: (
      <svg width={SZ} height={SZ} viewBox="0 0 40 40" aria-hidden>
        <polygon points="3,14 37,6 37,32 3,32" fill={C.amber.fill} stroke={C.amber.stroke} strokeWidth={1.5} />
      </svg>
    ),
  },
  {
    key: "trapezoid",
    label: "Trapezoid",
    preview: (
      <svg width={SZ} height={SZ} viewBox="0 0 40 40" aria-hidden>
        <polygon points="9,8 31,8 38,32 2,32" fill={C.teal.fill} stroke={C.teal.stroke} strokeWidth={1.5} />
      </svg>
    ),
  },
  {
    key: "offpage",
    label: "Off-Page",
    preview: (
      <svg width={SZ} height={SZ} viewBox="0 0 40 40" aria-hidden>
        <polygon points="4,4 36,4 36,24 20,37 4,24" fill={C.orange.fill} stroke={C.orange.stroke} strokeWidth={1.5} />
      </svg>
    ),
  },
  {
    key: "pentagon",
    label: "Pentagon",
    preview: (
      <svg width={SZ} height={SZ} viewBox="0 0 40 40" aria-hidden>
        <polygon points="20,3 37,16 30,37 10,37 3,16" fill={C.rose.fill} stroke={C.rose.stroke} strokeWidth={1.5} />
      </svg>
    ),
  },

  // ── Geometry / annotation ───────────────────────────────────────────────────
  {
    key: "star",
    label: "Star",
    preview: (
      <svg width={SZ} height={SZ} viewBox="0 0 40 40" aria-hidden>
        <polygon points="20,2 24.5,15 38,15 27,23.5 31,37 20,29 9,37 13,23.5 2,15 15.5,15" fill={C.amber.fill} stroke={C.amber.stroke} strokeWidth={1.5} />
      </svg>
    ),
  },
  {
    key: "octagon",
    label: "Octagon",
    preview: (
      <svg width={SZ} height={SZ} viewBox="0 0 40 40" aria-hidden>
        <polygon points="13,3 27,3 37,13 37,27 27,37 13,37 3,27 3,13" fill={C.sky.fill} stroke={C.sky.stroke} strokeWidth={1.5} />
      </svg>
    ),
  },
  {
    key: "callout",
    label: "Callout",
    preview: (
      <svg width={SZ} height={SZ} viewBox="0 0 40 40" aria-hidden>
        <path d="M4,5 H36 V25 H18 L11,36 L12,25 H4 Z" fill={C.indigo.fill} stroke={C.indigo.stroke} strokeWidth={1.5} strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    key: "brace",
    label: "Brace",
    preview: (
      <svg width={SZ} height={SZ} viewBox="0 0 40 40" aria-hidden>
        <path d="M28,3 Q16,3 16,11 Q16,20 6,20 Q16,20 16,29 Q16,37 28,37" fill="none" stroke={C.slate.stroke} strokeWidth={2} strokeLinecap="round" />
      </svg>
    ),
  },
  {
    key: "bracket",
    label: "Bracket",
    preview: (
      <svg width={SZ} height={SZ} viewBox="0 0 40 40" aria-hidden>
        <path d="M28,4 H14 V36 H28" fill="none" stroke={C.slate.stroke} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    key: "sticky_note",
    label: "Sticky Note",
    preview: (
      <svg width={SZ} height={SZ} viewBox="0 0 40 40" aria-hidden>
        <polygon points="5,5 29,5 35,11 35,35 5,35" fill={C.amber.fill} stroke={C.amber.stroke} strokeWidth={1.5} />
        <polygon points="29,5 29,11 35,11" fill={C.amber.stroke} opacity={0.5} />
      </svg>
    ),
  },
  {
    key: "highlight",
    label: "Highlight",
    preview: (
      <svg width={SZ} height={SZ} viewBox="0 0 40 40" aria-hidden>
        <rect x={3} y={12} width={34} height={16} rx={2} fill="rgba(250,204,21,0.4)" stroke="#facc15" strokeWidth={1.5} />
      </svg>
    ),
  },

  // ── Software architecture ───────────────────────────────────────────────────
  {
    key: "cube",
    label: "Cube",
    preview: (
      <svg width={SZ} height={SZ} viewBox="0 0 40 40" aria-hidden>
        <polygon points="4,12 26,12 26,36 4,36" fill={C.slate.fill} stroke={C.slate.stroke} strokeWidth={1.5} />
        <polygon points="4,12 12,4 34,4 26,12" fill={C.slate.fill} stroke={C.slate.stroke} strokeWidth={1.5} />
        <polygon points="26,12 34,4 34,28 26,36" fill={C.slate.fill} stroke={C.slate.stroke} strokeWidth={1.5} />
      </svg>
    ),
  },
  {
    key: "uml_component",
    label: "Component",
    preview: (
      <svg width={SZ} height={SZ} viewBox="0 0 40 40" aria-hidden>
        <rect x={9} y={7} width={28} height={26} fill={C.indigo.fill} stroke={C.indigo.stroke} strokeWidth={1.5} />
        <rect x={3} y={12} width={10} height={6} fill={C.indigo.fill} stroke={C.indigo.stroke} strokeWidth={1.5} />
        <rect x={3} y={22} width={10} height={6} fill={C.indigo.fill} stroke={C.indigo.stroke} strokeWidth={1.5} />
      </svg>
    ),
  },
  {
    key: "uml_package",
    label: "Package",
    preview: (
      <svg width={SZ} height={SZ} viewBox="0 0 40 40" aria-hidden>
        <polygon points="4,7 18,7 18,13 4,13" fill={C.emerald.fill} stroke={C.emerald.stroke} strokeWidth={1.5} />
        <rect x={4} y={13} width={32} height={22} fill={C.emerald.fill} stroke={C.emerald.stroke} strokeWidth={1.5} />
      </svg>
    ),
  },
  {
    key: "datastore",
    label: "Datastore",
    preview: (
      <svg width={SZ} height={SZ} viewBox="0 0 40 40" aria-hidden>
        <line x1={4} y1={11} x2={37} y2={11} stroke={C.teal.stroke} strokeWidth={1.5} />
        <line x1={4} y1={29} x2={37} y2={29} stroke={C.teal.stroke} strokeWidth={1.5} />
        <line x1={4} y1={11} x2={4} y2={29} stroke={C.teal.stroke} strokeWidth={1.5} />
        <line x1={11} y1={11} x2={11} y2={29} stroke={C.teal.stroke} strokeWidth={1.5} />
      </svg>
    ),
  },
  {
    key: "actor",
    label: "Actor",
    preview: (
      <svg width={SZ} height={SZ} viewBox="0 0 40 40" aria-hidden>
        <g fill="none" stroke={C.rose.stroke} strokeWidth={2} strokeLinecap="round">
          <circle cx={20} cy={8} r={5} />
          <line x1={20} y1={13} x2={20} y2={26} />
          <line x1={10} y1={18} x2={30} y2={18} />
          <line x1={20} y1={26} x2={12} y2={37} />
          <line x1={20} y1={26} x2={28} y2={37} />
        </g>
      </svg>
    ),
  },
  {
    key: "swimlane",
    label: "Swimlane",
    preview: (
      <svg width={SZ} height={SZ} viewBox="0 0 40 40" aria-hidden>
        <rect x={3} y={4} width={34} height={32} fill={C.slate.fill} stroke={C.slate.stroke} strokeWidth={1.5} />
        <line x1={3} y1={11} x2={37} y2={11} stroke={C.slate.stroke} strokeWidth={1.5} />
      </svg>
    ),
  },
  {
    key: "queue_buffer",
    label: "Queue",
    preview: (
      <svg width={SZ} height={SZ} viewBox="0 0 40 40" aria-hidden>
        <path d="M34,9 H8 V31 H34" fill={C.orange.fill} stroke={C.orange.stroke} strokeWidth={1.5} />
        <path d="M34,9 Q28,20 34,31" fill="none" stroke={C.orange.stroke} strokeWidth={1.5} />
      </svg>
    ),
  },

  // ── Connectors ──────────────────────────────────────────────────────────────
  {
    key: "dashed_line",
    label: "Dashed Line",
    preview: (
      <svg width={SZ} height={SZ} viewBox="0 0 40 40" aria-hidden>
        <line x1={5} y1={35} x2={35} y2={5} stroke={C.slate.stroke} strokeWidth={2} strokeDasharray="4 3" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    key: "double_arrow",
    label: "Double Arrow",
    preview: (
      <svg width={SZ} height={SZ} viewBox="0 0 40 40" aria-hidden>
        <line x1={9} y1={20} x2={31} y2={20} stroke={C.slate.stroke} strokeWidth={2} />
        <polygon points="3,20 10,16 10,24" fill={C.slate.stroke} />
        <polygon points="37,20 30,16 30,24" fill={C.slate.stroke} />
      </svg>
    ),
  },
  {
    key: "elbow",
    label: "Elbow",
    preview: (
      <svg width={SZ} height={SZ} viewBox="0 0 40 40" aria-hidden>
        <polyline points="5,8 5,30 30,30" fill="none" stroke={C.slate.stroke} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
        <polygon points="37,30 30,26 30,34" fill={C.slate.stroke} />
      </svg>
    ),
  },
];

// Small curated starter set shown before the user searches. Kept short (fits
// without scrolling) so the picker nudges toward search for the full library.
export const DEFAULT_SHAPE_KEYS = [
  "rectangle", "rounded_rect", "ellipse", "diamond", "cylinder", "pill",
];

// ── Lucide fallback previews for toolbar node items ───────────────────────────
// Re-export for backwards compat with old imports
export const SHAPE_ITEMS_WITH_LUCIDE = SHAPE_ITEMS.map((s, i) => ({
  ...s,
  lucidePreview: i === 0 ? <Type size={14} /> : i === 1 ? <Square size={14} /> : i === 2 ? <Circle size={14} /> : s.preview,
}));
